/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AcesVehicleAttributes } from '../api/types';
import { toSafeString } from '@/utils/toSafeString';

export const ymmeActionSources = {
  contentBlock: 'YMME' as const,
  vinLookup: 'VIN' as const,
  directURL: 'URL' as const,
  vehicleSearch: 'Vehicle Search' as const,
  storage: 'ymmeActionSource' as const,
};

export function prepareVehicleAttributes(params: {
  engineIds?: Record<string, object | string | number | undefined>;
  year?: number | string;
  [key: string]: object | string | number | undefined;
}): AcesVehicleAttributes {
  const { engineIds, year, ...rest } = params;

  const stringifyValues = (
    obj: Record<string, object | string | number | undefined>,
  ): Record<string, object | string | undefined> => {
    return Object.entries(obj).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          // If the value is an object (but not an array), process its properties
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Convert the object's values to strings
            acc[key] = Object.entries(
              value as Record<string, object | string | number | undefined>,
            ).reduce(
              (objAcc, [objKey, objValue]) => {
                if (objValue !== undefined && objValue !== null) {
                  objAcc[objKey] = toSafeString(objValue);
                }
                return objAcc;
              },
              {} as Record<string, string>,
            );
          } else {
            // Otherwise convert to string as before
            acc[key] = String(value);
          }
        }
        return acc;
      },
      {} as Record<string, object | string | undefined>,
    );
  };

  const result: Partial<AcesVehicleAttributes> = {};

  // Only include engineIds if it exists and has properties
  if (engineIds && Object.keys(engineIds).length > 0) {
    // Process engineIds values to strings
    result.engineIds = Object.entries(engineIds).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle objects properly instead of String(value)
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            acc[key] = Object.entries(value as Record<string, object | string | undefined>).reduce(
              (objAcc, [objKey, objValue]) => {
                if (objValue !== undefined && objValue !== null) {
                  objAcc[objKey] = toSafeString(objValue);
                }
                return objAcc;
              },
              {} as Record<string, string>,
            );
          } else {
            acc[key] = String(value);
          }
        }
        return acc;
      },
      {} as Record<string, object | string | undefined>,
    );
  }

  // Only include year if it's defined
  if (year !== undefined) {
    result.year = String(year);
  }
  // Add all other non-undefined values
  return {
    ...result,
    ...stringifyValues(rest),
  } as AcesVehicleAttributes;
}
